import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes
} from 'react';
import { tr } from '../lib/i18n';

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-muted">
      <svg width="16" height="16" viewBox="0 0 24 24" className="animate-spin" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="text-sm">{label ?? tr('Carregando')}</span>
    </span>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
  icon?: ReactNode;
};

export function Button({ variant = 'primary', loading, icon, children, className = '', disabled, ...rest }: ButtonProps): JSX.Element {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-5 py-3 transition-all duration-150 ' +
    'active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100';
  const styles: Record<string, string> = {
    primary: 'bg-accent text-on-accent shadow-[0_10px_20px_-12px_var(--nl-accent)] hover:bg-accent-strong',
    secondary: 'bg-card text-ink border border-line hover:bg-surface',
    ghost: 'bg-transparent text-ink hover:bg-surface',
    danger: 'bg-danger text-white shadow-[0_10px_20px_-12px_var(--nl-danger)] hover:bg-danger-strong'
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner label="" /> : icon}
      {children}
    </button>
  );
}

export function Card({ children, className = '', as: Tag = 'div', id }: { children: ReactNode; className?: string; as?: 'div' | 'section' | 'article'; id?: string }): JSX.Element {
  return <Tag id={id} className={`nl-card p-6 ${className}`}>{children}</Tag>;
}

export function Field({
  label, hint, error, required, children
}: { label: string; hint?: string; error?: string | null; required?: boolean; children: (props: { id: string; 'aria-describedby'?: string }) => ReactNode }): JSX.Element {
  const id = useId();
  const describedBy = hint || error ? `${id}-desc` : undefined;
  return (
    <div>
      <label className="nl-label" htmlFor={id}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {children({ id, 'aria-describedby': describedBy })}
      {hint && !error ? (
        <p id={`${id}-desc`} className="nl-hint mt-1">{hint}</p>
      ) : null}
      {error ? (
        <p id={`${id}-desc`} className="mt-1 text-sm text-danger flex items-center gap-1">
          <Icon name="alert" /> {error}
        </p>
      ) : null}
    </div>
  );
}

export function Input({ className = '', invalid, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }): JSX.Element {
  return <input className={`nl-input ${invalid ? 'border-danger' : ''} ${className}`} aria-invalid={invalid || undefined} {...rest} />;
}

export function Textarea({ className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea className={`nl-input ${className}`} {...rest} />;
}

export function Select({ className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return (
    <select className={`nl-input ${className}`} {...rest}>
      {children}
    </select>
  );
}

export function Alert({ tone = 'info', title, children }: { tone?: 'info' | 'success' | 'warning' | 'danger'; title?: string; children?: ReactNode }): JSX.Element {
  const tones: Record<string, string> = {
    info: 'border-line bg-card text-ink',
    success: 'border-sage bg-positive-soft text-positive-ink',
    warning: 'border-sand bg-warning-soft text-warning-ink',
    danger: 'border-danger bg-danger-soft text-danger-ink'
  };
  const icons: Record<string, JSX.Element> = {
    info: <Icon name="info" />,
    success: <Icon name="check" />,
    warning: <Icon name="alert" />,
    danger: <Icon name="alert" />
  };
  const label: Record<string, string> = { info: 'Aviso', success: 'Sucesso', warning: 'Atenção', danger: 'Erro' };
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={`rounded-2xl border px-4 py-3 text-sm flex gap-3 ${tones[tone]}`}>
      <span aria-hidden="true" className="mt-0.5 shrink-0">{icons[tone]}</span>
      <div>
        <p className="font-semibold">{title ?? tr(label[tone])}</p>
        {children ? <div className="mt-1 text-[0.95em] leading-relaxed">{children}</div> : null}
      </div>
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-card px-6 py-10 text-center">
      <p className="font-semibold">{title}</p>
      {description ? <p className="nl-hint mt-1">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function StatusPill({ tone, children }: { tone: 'neutral' | 'positive' | 'warning' | 'danger'; children: ReactNode }): JSX.Element {
  const tones: Record<string, string> = {
    neutral: 'bg-surface text-graphite',
    positive: 'bg-positive-soft text-positive-ink',
    warning: 'bg-warning-soft text-warning-ink',
    danger: 'bg-danger-soft text-danger-ink'
  };
  const dots: Record<string, string> = {
    neutral: 'bg-muted',
    positive: 'bg-sage',
    warning: 'bg-sand',
    danger: 'bg-danger'
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${tones[tone]}`}>
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${dots[tone]}`} />
      {children}
    </span>
  );
}

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[100dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-card shadow-lg sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-5 pb-2 pt-5">
          <h2 className="text-xl">{title}</h2>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-surface" aria-label={tr("Fechar")}>
            <Icon name="close" />
          </button>
        </div>
        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-2 ${
            footer ? 'pb-3' : 'pb-[calc(1.25rem+env(safe-area-inset-bottom))]'
          }`}
        >
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface Toast {
  id: number;
  tone: 'info' | 'success' | 'danger';
  message: string;
}

const ToastContext = createContext<(message: string, tone?: Toast['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, tone, message }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 left-1/2 z-[60] w-[min(92vw,28rem)] -translate-x-1/2 space-y-2" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`rounded-2xl border px-4 py-3 text-sm shadow-md ${t.tone === 'danger' ? 'border-danger bg-danger-soft text-danger-ink' : t.tone === 'success' ? 'border-sage bg-positive-soft text-positive-ink' : 'border-line bg-card'}`}>
            <span className="flex items-start gap-2">
              <span aria-hidden="true">{t.tone === 'danger' ? <Icon name="alert" /> : t.tone === 'success' ? <Icon name="check" /> : <Icon name="info" />}</span>
              {t.message}
            </span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (message: string, tone?: Toast['tone']) => void {
  return useContext(ToastContext);
}

const PATHS: Record<string, string> = {
  check: 'M20 6 9 17l-5-5',
  alert: 'M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  info: 'M12 16v-4m0-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  close: 'M18 6 6 18M6 6l12 12',
  calendar: 'M8 2v4m8-4v4M3 10h18M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
  clock: 'M12 8v4l3 2m5-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2m12-15a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
  chevron: 'm9 18 6-6-6-6',
  back: 'm15 18-6-6 6-6',
  menu: 'M4 6h16M4 12h16M4 18h16',
  plus: 'M12 5v14m-7-7h14',
  scissors: 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm15-18L8.12 13.88M14.8 14.8 21 21m-8.8-8.8L9 15',
  whatsapp: 'M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.2A9 9 0 1 0 12 3Z',
  chart: 'M4 20V10m6 10V4m6 16v-7m4 7H2',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3-1.6-.4a6.6 6.6 0 0 0-.6-1.5l.9-1.4-1.4-1.4-1.4.9a6.6 6.6 0 0 0-1.5-.6L15 4h-2l-.4 1.6a6.6 6.6 0 0 0-1.5.6l-1.4-.9L8.3 6.7l.9 1.4a6.6 6.6 0 0 0-.6 1.5L7 10v2l1.6.4c.14.53.34 1.03.6 1.5l-.9 1.4 1.4 1.4 1.4-.9c.47.26.97.46 1.5.6L13 19h2l.4-1.6c.53-.14 1.03-.34 1.5-.6l1.4.9 1.4-1.4-.9-1.4c.26-.47.46-.97.6-1.5L21 12Z',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14 5-5-5-5m5 5H9',
  shield: 'M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6l-8-3Z',
  star: 'm12 3 2.8 5.6 6.2.9-4.5 4.3 1.1 6.1L12 17.3 6.4 20.2l1.1-6.1L3 9.5l6.2-.9L12 3Z',
  search: 'm21 21-4.3-4.3M17 11a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.5-2.4 4-5.5 4-9s-1.5-6.6-4-9c-2.5 2.4-4 5.5-4 9s1.5 6.6 4 9ZM3 12h18',
  instagram: 'M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm5 5.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Zm5.5-1h.01',
  facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3V2Z',
  tiktok: 'M15 3h3a6 6 0 0 0 5 3v4a10 10 0 0 1-5-1.4V15a5.5 5.5 0 1 1-5.5-5.5h.5v4a1.5 1.5 0 1 0 2 1.4V3Z',
  share: 'M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M12 3v13m0-13 4 4m-4-4-4 4',
  map: 'M9 3 3 5.5v15L9 18l6 2.5 6-2.5v-15L15 6 9 3Zm0 0v15m6-12v15',
  link: 'M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1',
  pin: 'M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11Zm0-8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-14v2m0 18v-2M4.2 4.2l1.4 1.4m12.8 12.8 1.4 1.4M2 12h2m18 0h-2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M21 13.4A8.5 8.5 0 1 1 10.6 3a7 7 0 0 0 10.4 10.4Z'
};

export function Icon({ name, size = 18 }: { name: keyof typeof PATHS | string; size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={PATHS[name] ?? PATHS.info} />
    </svg>
  );
}
